import { CheckCircle, Circle } from 'lucide-react';
import {
  computeLifecycleStages,
  getTopLifecycleStage,
  type LifecycleStageInfo,
} from '../../utils/docugenLifecycle';

interface DocugenLifecycleBadgeProps {
  lead: {
    integrationId?: number | null;
    externalPlan?: string | null;
    externalCreatedAt?: string | null;
    externalDocsGenerated?: number | null;
    externalDocsUsed?: number | null;
    externalDocsLimit?: number | null;
    externalFirstDocAt?: string | null;
    externalLastActivityAt?: string | null;
    externalTriedLockedFeature?: boolean | null;
    externalTotalTokens?: number | null;
    stageType?: string | null;
  };
  variant?: 'compact' | 'timeline';
}

export function DocugenLifecycleBadge({ lead, variant = 'compact' }: DocugenLifecycleBadgeProps) {
  if (!lead.integrationId) {
    return null;
  }

  if (variant === 'compact') {
    return <CompactBadge lead={lead} />;
  }

  return <LifecycleTimeline lead={lead} />;
}

function CompactBadge({ lead }: { lead: DocugenLifecycleBadgeProps['lead'] }) {
  const top = getTopLifecycleStage(lead);
  if (!top) {
    return null;
  }

  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[10px] font-semibold rounded px-1.5 py-0.5 ${top.bgColor}`}
      title={top.detail || top.label}
    >
      {top.label}
    </span>
  );
}

function LifecycleTimeline({ lead }: { lead: DocugenLifecycleBadgeProps['lead'] }) {
  const stages = computeLifecycleStages(lead);
  const hasAnyReached = stages.some((s) => s.reached);
  if (!hasAnyReached) {
    return null;
  }

  return (
    <div className="px-6 pt-4">
      <h4 className="text-xs font-semibold text-secondary-500 uppercase tracking-wider mb-3">
        Engajamento Docugen
      </h4>
      <div className="space-y-0">
        {stages.map((stage, i) => (
          <TimelineStep key={stage.stage} stage={stage} isLast={i === stages.length - 1} />
        ))}
      </div>
    </div>
  );
}

function TimelineStep({ stage, isLast }: { stage: LifecycleStageInfo; isLast: boolean }) {
  return (
    <div className="flex items-start gap-2.5 relative">
      {!isLast && (
        <div
          className={`absolute left-[9px] top-[18px] w-px h-full ${
            stage.reached ? 'bg-secondary-200' : 'bg-secondary-100'
          }`}
        />
      )}
      <div className="shrink-0 mt-0.5">
        {stage.reached ? (
          <CheckCircle className={`w-[18px] h-[18px] ${stage.color}`} />
        ) : (
          <Circle className="w-[18px] h-[18px] text-secondary-200" />
        )}
      </div>
      <div className="pb-3 min-w-0">
        <p
          className={`text-sm font-medium ${
            stage.reached ? 'text-secondary-800' : 'text-secondary-300'
          }`}
        >
          {stage.label}
        </p>
        {stage.reached && (stage.detail || stage.date) && (
          <p className="text-xs text-secondary-400 mt-0.5">
            {[stage.date, stage.detail].filter(Boolean).join(' · ')}
          </p>
        )}
      </div>
    </div>
  );
}
